/*
 * Copyright (c) Nexign, JSC, 2022.
 */
/**
 * Page Reports Constructor Card component
 *
 * @author Evgeniy Naumov
 * @author Ilya Babanov
 * @version 1.22.0
 * @since 1.22.0
 */

import type { FC } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { makeSelectPageReportsConstructorTranslation } from '@modules/internationalization/intSelectors';

import { EConstructorItem } from '../constants';
import { reportsConstructorCurrentRowItemSelector, reportsConstructorSelectedItemSelector } from '../selector';
import { setCurrentRowItem, setSelectedItem } from '../slice';
import type { IConstructorReport } from '../types';
import ConstructorScheduleButton from './ConstructorScheduleButton';
import {
    ConstrucotorCardSubtitleStyled,
    ConstrucotorCardTitleStyled,
    ConstructorCardContainerStyled,
    ConstructorCardImageStyled,
    ConstructorCardStyled,
} from './styled/ConstructorStyled';

interface IProps {
    report: IConstructorReport;
    rowId: string;
}

export const ConstructorReportsCard: FC<IProps> = ({ rowId, report }) => {
    const { id, title, description, type, icon, schedule } = report;

    const dispatch = useDispatch();

    const currentRowItem = useSelector(reportsConstructorCurrentRowItemSelector);
    const selectedItem = useSelector(reportsConstructorSelectedItemSelector);
    const tReportsConstructor = useSelector(makeSelectPageReportsConstructorTranslation());

    const handleReportCardClick = () => {
        dispatch(setSelectedItem({ id, reportType: type, itemType: EConstructorItem.REPORT }));
    };

    const handleSheduleClick = () => {
        if (currentRowItem && currentRowItem.rowItemId === id && currentRowItem.rowId === rowId) {
            dispatch(setCurrentRowItem(null));
        } else {
            dispatch(
                setCurrentRowItem({
                    rowId,
                    rowItemId: id,
                    rowType: EConstructorItem.REPORT,
                    selectedItem: { id, reportType: type, itemType: EConstructorItem.REPORT },
                }),
            );
        }
    };

    return (
        <ConstructorCardStyled
            selected={selectedItem?.itemType === EConstructorItem.REPORT && selectedItem?.id === id}
            onClick={handleReportCardClick}
        >
            <ConstructorCardContainerStyled>
                <ConstrucotorCardTitleStyled>{title}</ConstrucotorCardTitleStyled>
                <ConstrucotorCardSubtitleStyled>{description}</ConstrucotorCardSubtitleStyled>
                {schedule && (
                    <ConstructorScheduleButton onClick={handleSheduleClick}>
                        {tReportsConstructor.common.schedule}
                    </ConstructorScheduleButton>
                )}
            </ConstructorCardContainerStyled>
            {icon && <ConstructorCardImageStyled alt="report-icon" src={icon} />}
        </ConstructorCardStyled>
    );
};
